/**
 * One store or product in its list: renamed in place, or merged into another.
 *
 * The two pages differ only in what they name and what a row links to, so they share this. A
 * merge deletes the row it starts from, which cannot be undone, so it takes two steps: choose
 * what to keep, then confirm with that name in the button.
 */

import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/problem";
import { Button, Input, Notice, Select } from "@/components/ui/Field";

interface Named {
  id: string;
  name: string;
}

export function CatalogRow({
  item,
  detail,
  title,
  targets,
  onRename,
  onMerge,
}: {
  item: Named;
  /** A line under the name: a count, an address. */
  detail: ReactNode;
  /** The name as shown, which may be a link to the item's own page. */
  title: ReactNode;
  targets: Named[];
  onRename: (name: string) => Promise<unknown>;
  onMerge: (intoId: string) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"view" | "rename" | "merge">("view");
  const [name, setName] = useState(item.name);
  const [intoId, setIntoId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const into = targets.find((target) => target.id === intoId);

  function reset() {
    setMode("view");
    setName(item.name);
    setIntoId("");
    setError(undefined);
  }

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(undefined);
    try {
      await action();
      reset();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("settings.errors.saveFailed"));
    } finally {
      setBusy(false);
    }
  }

  function rename() {
    const trimmed = name.trim();
    if (!trimmed || trimmed === item.name) {
      reset();
      return;
    }
    void run(() => onRename(trimmed));
  }

  return (
    <li className="space-y-2 px-4 py-3">
      {mode === "rename" ? (
        <div className="flex items-center gap-3">
          <Input
            value={name}
            autoFocus
            aria-label={t("catalog.nameOf", { name: item.name })}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") rename();
              if (event.key === "Escape") reset();
            }}
          />
          <Button type="button" className="w-auto px-3" disabled={busy} onClick={rename}>
            {t("common.save")}
          </Button>
        </div>
      ) : (
        <div>
          <div className="font-medium text-stone-900">{title}</div>
          <div className="text-sm text-stone-500">{detail}</div>
        </div>
      )}

      {mode === "merge" && (
        <div className="space-y-2">
          <Select
            value={intoId}
            aria-label={t("catalog.mergeInto", { name: item.name })}
            onChange={(event) => setIntoId(event.target.value)}
          >
            <option value="">{t("catalog.chooseKeep")}</option>
            {targets.map((target) => (
              <option key={target.id} value={target.id}>
                {target.name}
              </option>
            ))}
          </Select>
          {into && (
            <p className="text-sm text-stone-600">
              {t("catalog.mergeExplain", { name: item.name, into: into.name })}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              disabled={!into || busy}
              onClick={() => into && void run(() => onMerge(into.id))}
            >
              {into ? t("catalog.mergeConfirm", { into: into.name }) : t("catalog.merge")}
            </Button>
            <Button type="button" variant="secondary" onClick={reset}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      )}

      {mode === "view" && (
        <div className="flex gap-4 text-sm">
          <button
            type="button"
            className="text-brand-700 underline"
            onClick={() => setMode("rename")}
          >
            {t("common.rename")}
          </button>
          {targets.length > 0 && (
            <button
              type="button"
              className="text-brand-700 underline"
              onClick={() => setMode("merge")}
            >
              {t("catalog.merge")}
            </button>
          )}
        </div>
      )}

      {error && <Notice tone="error">{error}</Notice>}
    </li>
  );
}
